import { useState, type KeyboardEvent, type ReactElement } from 'react';
import { Alert, Box, Button, Card, CardContent, Stack, Text, TextField, BatchIcon } from '@citibox-panel/ui';
import { useRequestQrBatch } from '../hooks/useRequestQrBatch';
import { formatCount } from '../lib/format';
import type { QrBatchSeams } from '../lib/qrBatch';
import { parseQuantity } from '../lib/quantity';

export interface RequestBatchCardProps extends Pick<QrBatchSeams, 'requestQrBatch'> {
  // A batch is generating: the generator takes one at a time, so the form waits for it.
  isGenerating: boolean;
}

// TODO: i18n — copy en ES hasta Fase 1.
const FIELD_LABEL = 'Cantidad de etiquetas';
const RANGE_HINT = 'Entre 1 y 100.000 etiquetas';
const INVALID_HINT = 'Introduce un número entero entre 1 y 100.000';
const GENERATING_HINT = 'Hay un lote generándose. Podrás pedir otro cuando termine.';
// Fixed panel copy: the server's own `message`/`detail` never reaches the DOM.
const WRITE_ERROR = 'No se ha podido solicitar el lote. Inténtalo de nuevo en unos minutos.';

// The request: an amount and a button. No confirmation — a batch only produces a file, nothing is
// printed until someone sends it — but one batch at a time, which is the generator's own rule (the
// server answers 409 to a second). The field empties on success: the new row in the list IS the
// record, and a field that keeps the amount invites asking twice.
export function RequestBatchCard({ isGenerating, requestQrBatch }: RequestBatchCardProps): ReactElement {
  const [raw, setRaw] = useState('');
  const { request, isRequesting, outcome, isError } = useRequestQrBatch(requestQrBatch, {
    onRequested: () => {
      setRaw('');
    },
  });

  const amount = parseQuantity(raw);
  const isMistyped = raw.trim() !== '' && amount === null;
  // The server's refusal follows the amount it answers about, and goes away when that changes.
  const isRefused = outcome?.kind === 'invalid_amount' && amount === outcome.amount;
  // A 409 says the same thing as the disabled form, in the same words: the screen was behind.
  const showGenerating = isGenerating || outcome?.kind === 'pending_exists';
  const canSubmit = amount !== null && !isGenerating && !isRequesting;

  const submit = () => {
    if (canSubmit) request(amount);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') submit();
  };

  return (
    <Card variant="elevation" elevation={4} sx={{ borderRadius: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <BatchIcon fontSize="small" />
          <Text variant="subtitle2" component="h3" bold>
            Solicitar un lote
          </Text>
        </Box>
        <TextField
          label={FIELD_LABEL}
          value={raw}
          onChange={setRaw}
          onKeyDown={handleKeyDown}
          helperText={isMistyped || isRefused ? INVALID_HINT : RANGE_HINT}
          // The tint stays on the frame; the helper keeps the body tone, so the meaning is in the
          // words and never only in a colour.
          error={isMistyped || isRefused}
          maxLength={6}
          fullWidth
        />
        <Stack direction="row" sx={{ mt: 2 }}>
          <Button disabled={!canSubmit} loading={isRequesting} onClick={submit}>
            Generar
          </Button>
        </Stack>
        {showGenerating && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info">{GENERATING_HINT}</Alert>
          </Box>
        )}
        {/* Mounted in every state, so the region exists BEFORE its text arrives. */}
        <Box role="status" sx={{ mt: 2 }}>
          {outcome?.kind === 'requested' && (
            <Text variant="body2">
              {`Lote solicitado: ${formatCount(outcome.amount)} etiquetas. Se está generando; la lista se actualiza sola cada pocos segundos.`}
            </Text>
          )}
        </Box>
        {isError && (
          <Box sx={{ mt: 1 }}>
            <Alert severity="error">{WRITE_ERROR}</Alert>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
